import React, { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useCalendarData } from "../hooks/useCalendarData";

// Layout Components
import Header from "./layout/Header";
import UserMenu from "./layout/UserMenu";

// Calendar Components
import EventWheel from "./calendar/EventWheel";
import MonthPreview from "./calendar/MonthPreview";
import DateDisplay from "./calendar/DateDisplay";
import MobileMonthPreview from "./calendar/MobileMonthPreview";

// Chat Components
import ChatInterface from "./chat/ChatInterface";

// Common Components
import { LoadingScreen } from "./common/LoadingSpinner";

const Dashboard = () => {
	const { loading } = useAuth();
	const {
		events,
		loading: eventsLoading,
		setCurrentViewDate,
		fetchEvents,
		updateEvent,
		deleteEvent,
		createEvent,
	} = useCalendarData();
	const [currentViewDate, setCurrentViewDateLocal] = useState(new Date());
	const wheelRef = useRef(null);
	const lastDateChangeRef = useRef(null);
	const monthPreviewRef = useRef(null);
	const [isViewingCurrentTime, setIsViewingCurrentTime] = useState(true);

	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		if (!isInitialized) {
			setIsInitialized(true);
		}
	}, [isInitialized]);

	// Prevent scrolling on mount
	useEffect(() => {
		// Save original styles
		const originalStyle = window.getComputedStyle(document.body).overflow;

		// Prevent scrolling on body
		document.body.style.overflow = "hidden";

		// Optional: prevent touch move events
		const preventDefault = (e) => e.preventDefault();
		document.body.addEventListener("touchmove", preventDefault, {
			passive: false,
		});

		// Cleanup function
		return () => {
			document.body.style.overflow = originalStyle;
			document.body.removeEventListener("touchmove", preventDefault);
		};
	}, []);

	// Separate events into all-day and regular events with useMemo for performance
	const { allDayEvents, regularEvents } = React.useMemo(() => {
		return events.reduce(
			(acc, event) => {
				if (event.allDay) {
					acc.allDayEvents.push(event);
				} else {
					acc.regularEvents.push(event);
				}
				return acc;
			},
			{ allDayEvents: [], regularEvents: [] }
		);
	}, [events]);

	const handleEventCreate = useCallback(
		async (eventData) => {
			try {
				await createEvent(eventData);
				await fetchEvents(currentViewDate);
			} catch (error) {
				console.error("Failed to create event:", error);
				throw error;
			}
		},
		[createEvent, fetchEvents, currentViewDate]
	);

	const handleDateChange = useCallback(
		(date) => {
			const now = Date.now();
			if (lastDateChangeRef.current && now - lastDateChangeRef.current < 100) {
				return;
			}

			lastDateChangeRef.current = now;
			setCurrentViewDateLocal(date);
			setCurrentViewDate(date);
		},
		[setCurrentViewDate]
	);

	const handleReset = useCallback(() => {
		const now = new Date();

		if (wheelRef.current?.resetScroll) {
			wheelRef.current.resetScroll();
		}

		if (monthPreviewRef.current?.resetToToday) {
			monthPreviewRef.current.resetToToday();
		}

		setCurrentViewDateLocal(now);
		setCurrentViewDate(now);
		setIsViewingCurrentTime(true);

		// Only fetch if we're far from the current date
		const diffInDays = Math.abs(
			(currentViewDate - now) / (1000 * 60 * 60 * 24)
		);
		if (diffInDays > 80) {
			fetchEvents(now);
		}
	}, [setCurrentViewDate, fetchEvents, currentViewDate]);

	const handleDateSelect = useCallback(
		(date) => {
			// First update the local state
			setCurrentViewDateLocal(date);
			setCurrentViewDate(date);

			// Then scroll to the date
			if (wheelRef.current?.scrollToDate) {
				wheelRef.current.scrollToDate(date);
			}

			// Only fetch events if the selected date is outside our current window
			const daysDiff = Math.abs(
				(currentViewDate - date) / (1000 * 60 * 60 * 24)
			);
			if (daysDiff > 80) {
				fetchEvents(date);
			}
		},
		[setCurrentViewDate, fetchEvents, currentViewDate]
	);

	const handleEventUpdate = useCallback(
		async (updatedEvent) => {
			try {
				await updateEvent(updatedEvent);
			} catch (error) {
				console.error("Failed to update event:", error);
			}
		},
		[updateEvent]
	);

	const handleEventDelete = useCallback(
		async (eventId) => {
			try {
				await deleteEvent(eventId);
			} catch (error) {
				console.error("Failed to delete event:", error);
			}
		},
		[deleteEvent]
	);

	useEffect(() => {
		return () => {
			lastDateChangeRef.current = null;
		};
	}, []);

	if (loading) {
		return <LoadingScreen />;
	}

	return (
		<div className="fixed inset-0 flex flex-col bg-gray-50 dark:bg-gray-900 transition-colors duration-200 overflow-hidden touch-none">
			<div className="relative">
				<Header>
					<MobileMonthPreview
						ref={monthPreviewRef}
						onDateSelect={handleDateSelect}
						selectedDate={currentViewDate}
					/>
				</Header>
				{/* UserMenu in top right corner - Mobile only */}
				<div className="lg:hidden absolute top-[1.5vh] right-[1.5vh]">
					<UserMenu />
				</div>
			</div>

			<div className="flex flex-1 overflow-hidden">
				<div className="w-[45%] sm:w-80 flex flex-col border-r border-gray-200 dark:border-gray-700">
					<DateDisplay
						currentViewDate={currentViewDate}
						onReset={handleReset}
						isViewingCurrentTime={isViewingCurrentTime}
					/>
					<div className="flex-1 overflow-hidden">
						<EventWheel
							ref={wheelRef}
							events={regularEvents}
							allDayEvents={allDayEvents}
							currentDate={currentViewDate}
							onDateChange={handleDateChange}
							onEventUpdate={handleEventUpdate}
							onEventDelete={handleEventDelete}
							onEventCreate={handleEventCreate}
							isLoading={eventsLoading}
							onViewingCurrentTimeChange={setIsViewingCurrentTime}
						/>
					</div>
				</div>

				<div className="w-[55%] sm:flex-1 flex flex-col overflow-hidden">
					<div className="flex-1 overflow-hidden">
						<ChatInterface />
					</div>
				</div>

				<div className="w-80 border-l border-gray-200 dark:border-gray-700 hidden lg:block">
					<MonthPreview
						ref={monthPreviewRef}
						onDateSelect={handleDateSelect}
						selectedDate={currentViewDate}
					/>
				</div>
			</div>

			<div className="hidden lg:block absolute bottom-4 right-4">
				<UserMenu />
			</div>
		</div>
	);
};

export default Dashboard;
